import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

// Ordered stages while the assistant is preparing a response.
const STAGES = [
  { key: "submitted", label: "Submitted" },
  { key: "thinking", label: "Thinking" },
  { key: "reasoning", label: "Reasoning" },
  { key: "collecting", label: "Collecting data" },
  { key: "building", label: "Building response" },
];

// Cumulative auto-advance schedule (ms). Stage 0 starts immediately.
const STAGE_SCHEDULE_MS = [0, 700, 1800, 3500, 5500];

const LABEL_HEIGHT = 18; // px — single-line height that drives the ticker.

/**
 * Refined "thinking" indicator. No chrome — no pill, no border, no halo.
 * Just inline elements that read as a status, the way Claude / Linear /
 * Notion AI present generation state.
 *
 *   ● Building response       (with shimmer flowing through the text)
 *
 * Composition:
 *   - A 6px brand dot that softly pulses (opacity + outward halo).
 *   - A morphing stage label (vertical odometer) where the text itself
 *     carries a beautiful flowing brand-gradient — purple → violet → soft
 *     lavender — moving left → right, giving the words an alive, lit-from-
 *     within quality.
 *
 * Props:
 *   - hasContent: when true, jumps to the final stage. Caller is expected
 *     to unmount this component shortly after, once real streaming text
 *     appears.
 */
const ThinkingStages = ({ hasContent = false }) => {
  const [idx, setIdx] = useState(0);
  const timersRef = useRef([]);

  useEffect(() => {
    STAGE_SCHEDULE_MS.forEach((delay, i) => {
      if (i === 0) return;
      const t = setTimeout(() => {
        setIdx((cur) => Math.max(cur, i));
      }, delay);
      timersRef.current.push(t);
    });
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (hasContent) setIdx(STAGES.length - 1);
  }, [hasContent]);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={`Assistant is ${STAGES[idx].label.toLowerCase()}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        height: LABEL_HEIGHT,
        userSelect: "none",
      }}
    >
      <style>{`
        @keyframes prosmDotPulse {
          0%, 100% { opacity: 1;   transform: scale(1);   box-shadow: 0 0 0 0  rgba(107,47,160,0.45); }
          50%      { opacity: 0.7; transform: scale(0.8); box-shadow: 0 0 0 5px rgba(107,47,160,0); }
        }
        @keyframes prosmTextShimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -100% center; }
        }
      `}</style>

      {/* Pulsing brand dot */}
      <Box
        aria-hidden="true"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: "#6B2FA0",
          flexShrink: 0,
          animation: "prosmDotPulse 1.6s ease-in-out infinite",
        }}
      />

      {/* Morphing label — vertical odometer with shimmering brand gradient */}
      <Box
        sx={{
          position: "relative",
          height: LABEL_HEIGHT,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: `translateY(-${idx * LABEL_HEIGHT}px)`,
            willChange: "transform",
          }}
        >
          {STAGES.map((s) => (
            <Box
              key={s.key}
              sx={{
                height: LABEL_HEIGHT,
                lineHeight: `${LABEL_HEIGHT}px`,
                fontSize: "0.8125rem",
                fontWeight: 500,
                letterSpacing: "0.005em",
                whiteSpace: "nowrap",
                background:
                  "linear-gradient(90deg,#c4b5fd 0%,#9d6fff 30%,#6B2FA0 50%,#9d6fff 70%,#c4b5fd 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "prosmTextShimmer 2.4s linear infinite",
              }}
            >
              {s.label}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ThinkingStages;
